import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface ActionHintProps {
  label: string;
  children: React.ReactNode;
}

/**
 * Instant hover/focus label that portals above overflow-hidden table cells.
 * One short word. Not a native title delay.
 */
const ActionHint: React.FC<ActionHintProps> = ({ label, children }) => {
  const wrapRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; flip: boolean } | null>(
    null
  );
  const timerRef = useRef<number | null>(null);

  const place = () => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const flip = rect.top < 28;
    setPos({
      top: flip ? rect.bottom + 6 : rect.top - 6,
      left: rect.left + rect.width / 2,
      flip,
    });
  };

  const show = () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      place();
      setOpen(true);
    }, 60);
  };

  const hide = () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    setOpen(false);
  };

  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    },
    []
  );

  return (
    <>
      <span
        ref={wrapRef}
        className="inline-flex"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </span>
      {open &&
        pos &&
        createPortal(
          <span
            role="tooltip"
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              transform: pos.flip ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
              zIndex: 10000,
            }}
            className="pointer-events-none whitespace-nowrap rounded-md bg-primary-800 px-1.5 py-0.5 text-[10px] font-medium leading-none text-white shadow-sm"
          >
            {label}
          </span>,
          document.body
        )}
    </>
  );
};

export default ActionHint;
